import { useEffect, useState } from 'react';
import { Alert, Button, Modal, Spinner, Table } from 'react-bootstrap';
import { Eye } from 'react-bootstrap-icons';

import {
  getImportHistory,
  getImportIssues,
} from '../../routes/Partidas/partidasService';
import {
  formatDateTime,
  formatNumber,
} from '../../utils/partidasFormatters';

const typeLabel = (type) =>
  type === 'padron_articulos' ? 'Padrón de artículos' : 'Partidas activas';

const stateLabel = (state) =>
  ({
    pendiente: 'Pendiente',
    validando: 'Validando',
    procesando: 'Procesando',
    completada: 'Completada',
    completada_con_observaciones: 'Con observaciones',
    fallida: 'Fallida',
    cancelada: 'Cancelada',
  }[state] || state);

export default function HistorialImportaciones({ show, onHide, refreshKey }) {
  const [history, setHistory] = useState([]);
  const [issues, setIssues] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingIssues, setLoadingIssues] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show) return undefined;
    let active = true;
    setLoading(true);
    setError('');
    getImportHistory()
      .then((rows) => active && setHistory(rows))
      .catch((loadError) => active && setError(loadError.message))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [show, refreshKey]);

  const openIssues = async (item) => {
    setSelected(item);
    setIssues([]);
    setLoadingIssues(true);
    try {
      setIssues(await getImportIssues(item.id));
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoadingIssues(false);
    }
  };

  const close = () => {
    setSelected(null);
    setIssues([]);
    setError('');
    onHide();
  };

  return (
    <Modal show={show} onHide={close} size='xl' centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Historial de importaciones</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant='danger'>{error}</Alert>}
        {loading ? (
          <div className='partidas-loading-state'>
            <Spinner animation='border' /> Cargando historial...
          </div>
        ) : history.length === 0 ? (
          <div className='partidas-empty-state'>Todavía no hay importaciones registradas.</div>
        ) : (
          <div className='table-responsive d-none d-md-block'>
            <Table hover className='partidas-history-table align-middle'>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Tipo</th>
                  <th>Archivo</th>
                  <th>Estado</th>
                  <th>Leídas</th>
                  <th>Nuevas</th>
                  <th>Actualizadas</th>
                  <th>Ignoradas</th>
                  <th>Observaciones</th>
                </tr>
              </thead>
              <tbody>
                {history.map((item) => (
                  <tr key={item.id}>
                    <td>{formatDateTime(item.finalizado_at || item.iniciado_at)}</td>
                    <td>{typeLabel(item.tipo_archivo)}</td>
                    <td>{item.archivo_nombre}</td>
                    <td><span className={`partidas-import-state is-${item.estado}`}>{stateLabel(item.estado)}</span></td>
                    <td>{formatNumber(item.filas_leidas)}</td>
                    <td>{formatNumber(item.filas_nuevas)}</td>
                    <td>{formatNumber(item.filas_actualizadas)}</td>
                    <td>{formatNumber(item.filas_ignoradas)}</td>
                    <td>
                      <Button
                        variant='outline-primary'
                        size='sm'
                        disabled={!item.advertencias && !item.filas_con_error && !item.error_general}
                        onClick={() => openIssues(item)}
                      >
                        <Eye /> {formatNumber((item.advertencias || 0) + (item.filas_con_error || 0))}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {!error && !loading && history.length > 0 && (
          <div className='partidas-history-mobile-list d-md-none'>
            {history.map((item) => (
              <section key={item.id} className='partidas-history-mobile-card'>
                <div className='partidas-history-mobile-heading'>
                  <div>
                    <span>{typeLabel(item.tipo_archivo)}</span>
                    <strong>{item.archivo_nombre}</strong>
                  </div>
                  <span className={`partidas-import-state is-${item.estado}`}>
                    {stateLabel(item.estado)}
                  </span>
                </div>
                <div className='partidas-history-mobile-grid'>
                  <div><span>Fecha</span><strong>{formatDateTime(item.finalizado_at || item.iniciado_at)}</strong></div>
                  <div><span>Leídas</span><strong>{formatNumber(item.filas_leidas)}</strong></div>
                  <div><span>Nuevas</span><strong>{formatNumber(item.filas_nuevas)}</strong></div>
                  <div><span>Actualizadas</span><strong>{formatNumber(item.filas_actualizadas)}</strong></div>
                  <div><span>Ignoradas</span><strong>{formatNumber(item.filas_ignoradas)}</strong></div>
                </div>
                <Button
                  variant='outline-primary'
                  size='sm'
                  disabled={!item.advertencias && !item.filas_con_error && !item.error_general}
                  onClick={() => openIssues(item)}
                >
                  <Eye /> Ver observaciones ({formatNumber((item.advertencias || 0) + (item.filas_con_error || 0))})
                </Button>
              </section>
            ))}
          </div>
        )}

        {selected && (
          <section className='partidas-history-issues'>
            <h3>Observaciones de {selected.archivo_nombre}</h3>
            {selected.error_general && <Alert variant='danger'>{selected.error_general}</Alert>}
            {loadingIssues ? (
              <Spinner animation='border' size='sm' />
            ) : issues.length ? (
              <ul>
                {issues.map((issue) => (
                  <li key={issue.id} className={`is-${issue.severidad}`}>
                    <strong>{issue.severidad === 'error' ? 'Error' : 'Advertencia'}</strong>
                    {' · '}Fila {issue.numero_fila || '—'}: {issue.mensaje}
                  </li>
                ))}
              </ul>
            ) : (
              <p className='text-muted'>No hay observaciones de fila.</p>
            )}
          </section>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={close}>Cerrar</Button>
      </Modal.Footer>
    </Modal>
  );
}
